// components/Forms/NewsletterForm.jsx
'use client'

import { useState } from 'react'
import { sendNewsletterForm } from '@services/sendNewsletterForm' // ← adjust to wherever sendContactForm lives

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const pick = (v, lang, fallback) =>
  (v && typeof v === 'object') ? (v[lang] || v.en || fallback) : (v || fallback)

export default function NewsletterForm({ config, lang, userId, justify = 'center' }) {
  const [email, setEmail]   = useState('')
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('')

  const radius = `${config.inputRadius ?? 6}px`

  const successText = pick(config.successText, lang, 'Thanks for subscribing!')
  const errorText   = pick(config.errorText,   lang, 'Something went wrong. Please try again.')
  const invalidText = pick(config.invalidEmailText, lang, 'Please enter a valid email address.')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (status === 'submitting') return

    const trimmed = email.trim()
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('error')
      setErrorMsg(invalidText)
      return
    }

    setStatus('submitting')
    setErrorMsg('')

    try {
      const fbclid = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('fbclid') ?? undefined
        : undefined

      await sendNewsletterForm({ userId, email: trimmed, lang, fbclid })
      setStatus('success')
      setEmail('')
    } catch (err) {
      console.error('Newsletter signup error:', err)
      setStatus('error')
      setErrorMsg(errorText)
    }
  }

  if (status === 'success') {
    return (
      <p role="status" style={{ color: config.subtitleColor ?? 'rgba(255,255,255,0.9)', fontWeight: 600, margin: 0 }}>
        {successText}
      </p>
    )
  }

  const submitting = status === 'submitting'

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{ display: 'flex', gap: '0.5rem', maxWidth: '420px', margin: '0 auto', flexWrap: 'wrap', justifyContent: justify }}
    >
      <input
        type="email"
        name="email"
        required
        autoComplete="email"
        aria-label={pick(config.inputPlaceholder, lang, 'Your email address')}
        placeholder={pick(config.inputPlaceholder, lang, 'Your email address')}
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle') }}
        disabled={submitting}
        style={{ flex: 1, minWidth: '200px', padding: '0.625rem 1rem', borderRadius: radius, border: 'none', fontSize: '0.9375rem' }}
      />
      <button
        type="submit"
        disabled={submitting}
        style={{
          padding: '0.625rem 1.5rem',
          borderRadius: radius,
          background: config.submitBg ?? '#ffffff',
          color: config.submitColor ?? 'var(--color-main)',
          fontWeight: 700, border: 'none', whiteSpace: 'nowrap',
          cursor: submitting ? 'wait' : 'pointer',
          opacity: submitting ? 0.7 : 1,
        }}
      >
        {submitting ? '…' : pick(config.submitText, lang, 'Subscribe')}
      </button>

      {status === 'error' && errorMsg && (
        <p role="alert" style={{ flexBasis: '100%', margin: '0.25rem 0 0', fontSize: '0.875rem', color: config.errorColor ?? '#fecaca' }}>
          {errorMsg}
        </p>
      )}
    </form>
  )
}